import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Input } from 'antd';
import type { MenuProps } from 'antd';
import { AppstoreOutlined, SendOutlined } from '@ant-design/icons';

import { useXoeTools } from '../../hooks/xoe/useXoe';
import type { XoeActionKind } from '../../types/xoe/XoeTypes';
import type { XoeComposerProps } from '../../types/xoe/XoeUiTypes';
import { useXoeSize } from './XoeSurface';

const { TextArea } = Input;

const TOOL_GROUPS: { kind: XoeActionKind; label: string }[] = [
	{ kind: 'write', label: 'Create' },
	{ kind: 'open', label: 'Open' },
	{ kind: 'external', label: 'Search external' },
];

const XoeComposer: React.FC<XoeComposerProps> = ({
	onSend,
	disabled = false,
	conversationUuid,
	dictatedText,
	dictationToken,
	insertedText,
	insertToken,
}) => {
	const size = useXoeSize();
	const compact = size === 'compact';
	const [text, setText] = useState('');
	const [focused, setFocused] = useState(false);
	const textAreaRef = useRef<any>(null);
	const lastDictationToken = useRef<number | undefined>(undefined);
	const lastInsertToken = useRef<number | undefined>(undefined);

	const appendAndFocus = useCallback((addition: string) => {
		if (!addition) return;
		setText((prev) => {
			const base = prev.trimEnd();
			return base ? `${base} ${addition}` : addition;
		});
		textAreaRef.current?.focus?.();
	}, []);

	// Keyed off the token, not the text, so the same phrase appends twice.
	useEffect(() => {
		if (dictationToken === undefined || dictationToken === lastDictationToken.current) return;
		lastDictationToken.current = dictationToken;
		if (dictatedText) appendAndFocus(dictatedText);
	}, [dictationToken, dictatedText, appendAndFocus]);

	useEffect(() => {
		if (insertToken === undefined || insertToken === lastInsertToken.current) return;
		lastInsertToken.current = insertToken;
		if (insertedText) appendAndFocus(insertedText);
	}, [insertToken, insertedText, appendAndFocus]);

	const showTools = !!conversationUuid;
	const { data: tools } = useXoeTools(conversationUuid ?? '');

	const handleSend = useCallback(() => {
		const trimmed = text.trim();
		if (!trimmed || disabled) return;
		onSend(trimmed);
		setText('');
	}, [text, disabled, onSend]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleSend();
			}
		},
		[handleSend],
	);

	const toolMenuItems = useMemo<MenuProps['items']>(() => {
		if (!tools || tools.length === 0) return [];
		return TOOL_GROUPS.flatMap((group) => {
			const groupTools = tools.filter((t) => t.kind === group.kind);
			if (groupTools.length === 0) return [];
			return [
				{
					type: 'group' as const,
					key: `group-${group.kind}`,
					label: group.label,
					children: groupTools.map((tool) => ({
						key: tool.action_type,
						disabled: tool.enabled === false,
						label: (
							<div className="mg-xoe-composer-tool">
								<div className="mg-xoe-composer-tool-label">{tool.label}</div>
								{tool.description && <div className="mg-xoe-composer-tool-desc">{tool.description}</div>}
							</div>
						),
					})),
				},
			];
		});
	}, [tools]);

	// Inserts the editable starter, never invokes the action: propose-and-confirm depends on it.
	const handleToolSelect = useCallback<NonNullable<MenuProps['onClick']>>(
		({ key }) => {
			const tool = tools?.find((t) => t.action_type === key);
			if (!tool || tool.enabled === false) return;
			appendAndFocus(tool.starter_template || '');
		},
		[tools, appendAndFocus],
	);

	const showHint = !compact && (focused || text.length > 0);

	return (
		<div className="mg-xoe-composer-root" data-testid="xoe-composer" data-disabled={String(disabled)}>
			<div className="mg-xoe-composer">
				<TextArea
					ref={textAreaRef}
					className="mg-xoe-composer-input"
					value={text}
					onChange={(e) => setText(e.target.value)}
					onKeyDown={handleKeyDown}
					onFocus={() => setFocused(true)}
					onBlur={() => setFocused(false)}
					placeholder={disabled ? 'Xoe is thinking…' : 'Ask Xoe…'}
					disabled={disabled}
					variant="borderless"
					autoSize={{ minRows: 1, maxRows: compact ? 3 : 8 }}
					data-testid="xoe-composer-input"
				/>

				{showTools && (
					<Dropdown
						trigger={['click']}
						placement="topRight"
						disabled={disabled}
						menu={{ items: toolMenuItems, onClick: handleToolSelect }}
					>
						<Button
							type="text"
							shape="circle"
							icon={<AppstoreOutlined />}
							disabled={disabled}
							aria-label="Insert a tool command"
							data-testid="xoe-composer-tools"
						/>
					</Dropdown>
				)}

				<Button
					type="primary"
					shape="circle"
					icon={<SendOutlined />}
					onClick={handleSend}
					disabled={disabled || !text.trim()}
					aria-label="Send message"
					data-testid="xoe-composer-send"
				/>
			</div>

			{showHint && (
				<div className="mg-xoe-composer-hint" data-testid="xoe-composer-hint">
					Enter to send · Shift + Enter for a new line
				</div>
			)}
		</div>
	);
};

export default XoeComposer;
